// Type-only: AIInsightsModal.utils imports this module at runtime, so a value import here
// would create a require cycle.
import type { A2uiOperation } from '~/components/AIInsightsModal/AIInsightsModal.utils';

// Must match the agent's a2ui_surfaces.py VISITIQ_CATALOG_ID.
export const VISITIQ_CATALOG_ID = 'visitiq://components/v1';

const A2UI_VERSION = 'v0.9';

// Raw generate_dynamic_ui tool input as persisted and returned by the API in
// ConversationMessage.a2ui (already camelCased by the API read path). `surfaceId` is the
// server-derived form id ("form:<toolUseId>"); the API always sets it.
export interface A2uiSurfacePayload {
  components: unknown[];
  dataModel?: Record<string, unknown> | null;
  surfaceId: string;
}

// Rebuilds the A2UI v0.9 operations envelope from a persisted surface payload. Exact mirror
// of the agent's a2ui_surfaces.build_operations_envelope: include updateDataModel whenever the
// data model is present (an empty object is a valid model that initializes/clears the surface
// state), skipping it only for an absent (null/undefined) model — ordering matters for the
// client processor.
export const buildOperationsEnvelope = ({ components, dataModel, surfaceId }: A2uiSurfacePayload): A2uiOperation[] => [
  { version: A2UI_VERSION, createSurface: { surfaceId, catalogId: VISITIQ_CATALOG_ID } },
  ...(dataModel != null
    ? [{ version: A2UI_VERSION, updateDataModel: { surfaceId, path: '/', value: dataModel } }]
    : []),
  { version: A2UI_VERSION, updateComponents: { surfaceId, components } },
];

// Overlays a submission's context values onto a surface's operations so a reloaded, already
// submitted form renders with the values the user entered. Merges over the root
// updateDataModel value, or inserts the op after createSurface when the surface had no
// initial data model. Returns a new array; never mutates the input operations.
export const mergeSubmittedValues = (
  operations: A2uiOperation[],
  surfaceId: string,
  context: Record<string, unknown>,
): A2uiOperation[] => {
  const dataModelIndex = operations.findIndex((op) => {
    const update = op.updateDataModel as { surfaceId?: string; path?: string } | undefined;
    return update?.surfaceId === surfaceId && update?.path === '/';
  });

  if (dataModelIndex >= 0) {
    const existing = operations[dataModelIndex].updateDataModel as { value?: Record<string, unknown> };
    const merged = {
      ...operations[dataModelIndex],
      updateDataModel: { ...existing, value: { ...existing.value, ...context } },
    };
    return [...operations.slice(0, dataModelIndex), merged, ...operations.slice(dataModelIndex + 1)];
  }

  const createIndex = operations.findIndex(
    (op) => (op.createSurface as { surfaceId?: string } | undefined)?.surfaceId === surfaceId,
  );
  const insertAt = createIndex >= 0 ? createIndex + 1 : 0;
  const dataModelOp = { version: A2UI_VERSION, updateDataModel: { surfaceId, path: '/', value: context } };
  return [...operations.slice(0, insertAt), dataModelOp, ...operations.slice(insertAt)];
};

// Components that keep their value empty while it can't be sent: a form with one is submitted only once it is filled
const SELF_VALIDATING_COMPONENTS = new Set(['VIQMatchingRules', 'VIQCustomFields']);
const REQUIRED_CHECK_MESSAGE = 'Fix the highlighted fields first.';

type SurfaceComponent = Record<string, unknown> & { component?: unknown; value?: unknown; action?: unknown };
type ButtonCheck = { condition?: unknown; message?: unknown };

/**
 * The operations a surface's A2UI store is fed, built from their serialized form:
 * - a copy, since the store keeps the data model it receives by reference and writes form edits into it. The message's
 *   operations stay as the agent sent them, and a surface rendered again from them (the streamed message re-rendered
 *   once complete, a history reload) starts from the agent's values;
 * - each submit Button gets a `required` check on every self-validating component's value, so it stays disabled while
 *   one can't be sent, whatever checks the agent wrote. Checks without a `condition` are dropped.
 */
export const prepareOperationsForStore = (serializedOperations: string): A2uiOperation[] =>
  (JSON.parse(serializedOperations) as A2uiOperation[]).map((operation) => {
    const update = operation.updateComponents as { components?: SurfaceComponent[] } | undefined;
    if (!Array.isArray(update?.components)) return operation;
    const requiredPaths = update.components.flatMap(({ component, value }) => {
      const path = (value as { path?: unknown } | undefined)?.path;
      return typeof component === 'string' && SELF_VALIDATING_COMPONENTS.has(component) && typeof path === 'string'
        ? [path]
        : [];
    });
    return {
      ...operation,
      updateComponents: {
        ...update,
        components: update.components.map((item) => withRequiredChecks(item, requiredPaths)),
      },
    };
  });

const withRequiredChecks = (item: SurfaceComponent, requiredPaths: string[]): SurfaceComponent => {
  if (item.component !== 'Button' || !(item.action as { event?: unknown } | undefined)?.event) return item;
  const checks = (Array.isArray(item.checks) ? (item.checks as ButtonCheck[]) : []).filter(
    (check) => check && typeof check === 'object' && 'condition' in check,
  );
  const missingPaths = requiredPaths.filter((path) => !checks.some((check) => isRequiredCheckOn(check, path)));
  const allChecks = [
    ...checks,
    ...missingPaths.map((path) => ({
      condition: { call: 'required', args: { value: { path } } },
      message: REQUIRED_CHECK_MESSAGE,
    })),
  ];
  return allChecks.length || item.checks !== undefined ? { ...item, checks: allChecks } : item;
};

const isRequiredCheckOn = ({ condition }: ButtonCheck, path: string) => {
  const call = condition as { call?: unknown; args?: { value?: { path?: unknown } } } | undefined;
  return call?.call === 'required' && call.args?.value?.path === path;
};
